import './indexdb-polyfill.js';
import { default as _log } from '@dw/loglevel';
import CouchDB from "./couchdb.js";
import EventEmitter from "@dreamworld/event-emitter";
import reducer from "./redux/reducer.js";
import { uuidBase62 } from '@dreamworld/uuid-base62';
import * as _selectors from './redux/selectors.js';
import * as _utils from './utils.js';
import { AuthStatus as _authStatus } from './auth-status.js';

// Set logger
const log = _log.getLogger('dreamdb');
_log.setLevel(_log.levels.WARN);

class DreamDB extends EventEmitter {

  constructor() {
    super();
    /**
     * A redux store which holds the whole state tree of dreamDb.
     */
    this.store = undefined;

    /**
     * Holds CouchDBs. key is the name of the couchdb, and value is it's instance.
     * Default database is stored under the key `default`.
     */
    this.couchdbs = {};

    this._deviceId = null;

    this._onCouchdbDestroyed = this._onCouchdbDestroyed.bind(this);
  }

  /**
   * This method is use to intialize redux with store.
   * If this method is not called by the integrator, then redux intialization will be skipped.
   * @param {Object} store store
   */
  connectToRedux(store) {

    if (!store || typeof store !== 'object' || typeof store.dispatch !== "function" || typeof store.getState !== "function") {
      log.error(`dreamDB.connectToRedux(): Argument "store" is invalid, It must be valid redux store`);
      return;
    }

    this.store = store;

    // Add reducer
    this.store.addReducers({
      dreamdb: reducer
    });
  }

  /**
   * It's used to init socket connection with the given URL.
   * 
   * @param {String} url - socket url of couchdb-gateway to connect with.
   * @param {String} name - name of couchdb. If not provided then name will be `default`.
   * @returns {Object} returns couchdb instance.
   */
  connect(url, name = "default") {
    if (this.couchdbs[name]) {
      log.error("dreamDB.connect(): couchDb with same name already exists. couchDb name = ", name);
      return;
    }

    this._setDeviceId();
    let couchdb = new CouchDB(name, url, this.store, this._deviceId);
    couchdb.on(`destroyed`, this._onCouchdbDestroyed);
    this.couchdbs[name] = couchdb;

    return couchdb;
  }

  /**
   * Used to get couchdb instance of given name.
   * 
   * @param {String} name - Name of couchdb. If not provided returns counch db instance named with `default`
   * @returns {Object} returns couchdb instance
   */
  couchdb(name) {
    let couchdbName = name || 'default';

    return this.couchdbs[couchdbName];
  }

  /**
   * Returns current deviceId
   */
  deviceId() {
    return this._deviceId;
  }

  /**
   * It's couchdb's `destoyed` event handler.
   * It removes couchdb instance from in-memory.
   * @param {Object} details 
   */
  _onCouchdbDestroyed({ name }) {
    this.couchdbs[name].off(`destroyed`, this._onCouchdbDestroyed);
    delete this.couchdbs[name];
    log.info(`_onCouchdbDestroyed: couchdb destroyed. name=${name}`);
  }

  /**
   * Sets current device id
   */
  _setDeviceId() {
    const deviceId = localStorage.getItem('dreamdb.deviceId');

    if (!deviceId) {
      this._deviceId = `${uuidBase62()}-${uuidBase62()}`;
      window.localStorage.setItem(`dreamdb.deviceId`, this._deviceId);
      return;
    }

    this._deviceId = deviceId;
  }

  /**
   * Stops listening for the live changes for the given queryIdPrefix.
   * It calls couchDbs `cancelAll` method
   * 
   * @param {String} queryIdPrefix - prefix of the queryId
   */
  cancelAll(queryIdPrefix) {
    if (!queryIdPrefix) {
      log.error(`dreamDB.cancelAll(): queryIdPrefix is required.`);
      return;
    }

    if (typeof queryIdPrefix !== 'string') {
      log.error(`dreamDB.cancelAll(): queryIdPrefix should be type of String.`);
      return;
    }

    forEach(this.couchdbs, (couchdbRef) => {
      couchdbRef.cancelAll(queryIdPrefix);
    });

    log.info(`dreamDB.cancelAll(): All queries from all the couchdbs has been cancelled which is startsWith=${queryIdPrefix}.`);
  }
}

const dreamdb = new DreamDB();

/**
 * Set `deviceId`, before initialization.
 */
dreamdb._setDeviceId();

export default dreamdb;
export const selectors = _selectors;
export const AuthStatus = _authStatus;
export const utils = _utils;